/* eslint-disable complexity */
import { unionBy } from 'lodash';

import { DESCENDING } from 'components/Common/DataTableV2/constants';

import { SIX_YEARS_GRADUATION_RATE_SORT_KEY } from 'modules/SchoolSearch/constants';

const mergeUpdateByProp = (arr, obj, prop, preMerge = false) => {
  const i = arr.findIndex(el => el[prop] === obj[prop]);
  const returnValue = {};
  if (i === -1) {
    returnValue.value = preMerge ? [obj, ...arr] : [...arr, obj];
    returnValue.added = true;
  }
  returnValue.value = [...arr.slice(0, i), obj, ...arr.slice(i + 1)];
  return returnValue;
};

const updateByProp = (arr, uniqId, prop, update) => {
  const i = arr.findIndex(el => el[prop] === uniqId);

  if (i === -1) return arr;

  return [...arr.slice(0, i), { ...arr[i], ...update }, ...arr.slice(i + 1)];
};

const customUpdateSchoolProgram = ({
  schools,
  schoolUUID,
  schoolUpdate,
  programUUID,
  programUpdate,
}) => {
  const { programs = [] } = schools.find(el => el.uuid === schoolUUID);
  return updateByProp(schools, schoolUUID, 'uuid', {
    ...schoolUpdate,
    programs: updateByProp(programs, programUUID, 'uuid', programUpdate),
  });
};

const customUnfavouriteSchool = ({ schools, uuid, update }) => {
  const { programs = [] } = schools.find(el => el.uuid === uuid);
  return updateByProp(schools, uuid, 'uuid', {
    ...update,
    programs: programs.map(el => ({ ...el, ...update })),
  });
};

export default (
  state = {
    // ui
    viewType: 'split', // can be 'list', 'split', 'map'
    hoveredSchool: null,
    // api
    context: null,
    busy: false,
    page: 0,
    count: 0,
    favoriteProgramCount: 0,
    favoriteSchoolCount: 0,
    schools: null,
    // sort n filter
    sort: {
      order: DESCENDING,
      by: SIX_YEARS_GRADUATION_RATE_SORT_KEY,
    },
    filter: {},
    searchFilter: {},
    areaFilter: null, // bounding_box=`min_lat,max_lat,min_lng,max_lng`

    // default filters
    defaultProgram: null,
    defaultProgramType: null,
    defaultSorting: null,

    // scattergram
    activeScattergramSchool: null,
    scattergram: null,

    // snack
    snackOpen: false,
    snackSchool: {},

    // school list
    schoolLists: null,

    reasonsForFavoriting: [],
    defaultTestType: null,
    defaultGpaType: null,
  },
  { type, ...rest },
) => {
  switch (type) {
    case 'SCHOOL_SEARCH/SET_CONTEXT':
      return {
        ...state,
        context: rest.context,
      };

    case 'SCHOOL_SEARCH/TOGGLE_BUSY':
      return {
        ...state,
        busy: !state.busy,
      };

    case 'SCHOOL_SEARCH/INC_PAGE':
      return {
        ...state,
        page: state.page + 1,
      };

    case 'SCHOOL_SEARCH/MERGE_SCHOOLS':
      return {
        ...state,
        schools: unionBy(state.schools, rest.results, 'pk'),
        count: rest.count,
        favoriteProgramCount: rest.favoriteProgramCount,
        favoriteSchoolCount: rest.favoriteSchoolCount,
      };

    case 'SCHOOL_SEARCH/MERGE_SCHOOL':
      return {
        ...state,
        schools: mergeUpdateByProp(state.schools, rest.result, 'pk'),
      };

    case 'SCHOOL_SEARCH/UPDATE_SCHOOL':
      return {
        ...state,
        schools: updateByProp(state.schools, rest.uuid, 'uuid', rest.update),
      };

    case 'SCHOOL_SEARCH/UNFAVOURITE_SCHOOL':
      return {
        ...state,
        schools: customUnfavouriteSchool({ schools: state.schools, ...rest }),
      };

    case 'SCHOOL_SEARCH/UPDATE_SCHOOL_PROGRAM':
      return {
        ...state,
        schools: customUpdateSchoolProgram({ schools: state.schools, ...rest }),
      };

    case 'SCHOOL_SEARCH/RESET_SCHOOLS':
      return {
        ...state,
        schools: rest.results,
        count: rest.count,
        favoriteProgramCount: rest.favoriteProgramCount,
        favoriteSchoolCount: rest.favoriteSchoolCount,
        page: 1,
      };

    case 'SCHOOL_SEARCH/SET_SORT':
      return {
        ...state,
        sort: rest.sort,
      };

    case 'SCHOOL_SEARCH/SET_FILTER':
      return {
        ...state,
        filter: rest.filter,
      };

    case 'SCHOOL_SEARCH/SET_SEARCH_FILTER':
      return {
        ...state,
        searchFilter: rest.searchFilter,
      };

    case 'SCHOOL_SEARCH/SET_AREA_FILTER':
      return {
        ...state,
        areaFilter: rest.areaFilter,
      };

    case 'SCHOOL_SEARCH/SET_VIEW_TYPE':
      return {
        ...state,
        viewType: rest.viewType,
      };

    case 'SCHOOL_SEARCH/SET_HOVERED_SCHOOL':
      return {
        ...state,
        hoveredSchool: rest.hoveredSchool,
      };
    case 'SCHOOL_SEARCH/SET_ACTIVE_SCATTERGRAM_SCHOOL':
      return {
        ...state,
        activeScattergramSchool: rest.activeSchool,
        scattergram: !rest.activeSchool ? null : state.scattergram,
      };
    case 'SCHOOL_SEARCH/POPULATE_SCATTERGRAM_DATA':
      return {
        ...state,
        scattergram: {
          ...rest.scattergramData,
          schoolData: rest.scattergramData.historicalData,
          districtSchoolData: rest.scattergramData.districtHistoricalData,
        },
      };

    case 'SEARCH_COLLEGES_WITH_PROGRAM':
      return {
        ...state,
        defaultProgram: rest.data.program,
        defaultProgramType: Object.keys(rest.data.programType)[0],
        defaultSorting: rest.data.sorting,
      };

    case 'SCHOOL_SEARCH/SET_SNACKBAR_MODE':
      return {
        ...state,
        snackOpen: true,
        snackSchool: rest.snackSchool,
      };

    case 'SCHOOL_SEARCH/SET_SNACKBAR_CLOSED':
      return {
        ...state,
        snackOpen: false,
      };

    case 'SCHOOL_SEARCH/SET_SCHOOL_LISTS':
      return {
        ...state,
        schoolLists: rest.data,
      };

    case 'SCHOOL_SEARCH/MERGE_SCHOOLS_FOR_SCHOOL_LIST':
      return {
        ...state,
        schools: rest.schools,
      };

    case 'SCHOOL_SEARCH/SET_REASONS_FOR_FAVORITING':
      return {
        ...state,
        reasonsForFavoriting: rest.data,
      };

    case 'SCHOOL_SEARCH/SET_DEFAULT_TEST_TYPE':
      return {
        ...state,
        defaultTestType: rest.data,
      };

    case 'SCHOOL_SEARCH/SET_DEFAULT_GPA_TYPE':
      return {
        ...state,
        defaultGpaType: rest.data,
      };

    default:
      return state;
  }
};
